<template>
  <section id="transactions-panel">
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <div class="page-inner">
      <div class="content-panel">
        <store-header
          v-if="storeBeingManaged && storeBeingManaged._id"
          :storeBeingManaged="storeBeingManaged"
        />

        <transactions-title
          :storeId="(storeBeingManaged && storeBeingManaged._id) || ''"
          :storeName="
            (storeBeingManaged &&
              storeBeingManaged.storeInfo &&
              storeBeingManaged.storeInfo.name) ||
            ''
          "
          @update-dates="updateDates"
          @update-store="updateStore"
          @get-transactions="getTransactions"
          @download-transactions-report="downloadTransactionsReport"
        />

        <h2
          v-if="transactionsDisplay.length === 0 && !isLoading"
          class="empty-state"
        >
          Não existem transações neste período para esta loja.
        </h2>

        <template v-else>
          <transactions-summary :transactions="transactionsDisplay" />

          <div
            v-if="
              !permissions.canSeeUserPersonalId &&
              !permissions.canSeeUserEmail &&
              !permissions.canSeeUserCellphone
            "
            class="lgpd-notice"
          >
            Em concordância com a Lei 13.709 - Lei Geral de Proteção de Dados
            Pessoais (LGPD) - e em respeito à privacidade dos nossos usuários,
            não são exibidas aqui informações pessoais.
          </div>

          <div v-if="!showTransactions" class="see-transactions-wrap">
            <button
              class="see-transactions-btn"
              @click="showTransactions = true"
            >
              <span class="material-icons see-icon">list_alt</span>
              Ver transações ({{ transactionsDisplay.length }})
            </button>
          </div>

          <template v-else>
            <transactions-table :transactions="transactionsPaged" />

            <pagination
              v-if="totalPages > 1"
              :currentPage="currentPage"
              :totalPages="totalPages"
              @update:page="onPageChange"
            />
          </template>
        </template>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../mixins/common";
import { mask } from "../../mixins/mask";
import storeHeader from "../common/StoreHeader";
import transactionsTitle from "./TransactionsTitle";
import transactionsSummary from "./transactions-summary/TransactionsSummary";
import transactionsTable from "./transactions-table/TransactionsTable";
import pagination from "../common/Pagination";

export default {
  mixins: [common, mask],

  components: {
    storeHeader,
    transactionsTitle,
    transactionsSummary,
    transactionsTable,
    pagination
  },

  data() {
    return {
      isLoading: false,
      dates: [],
      store: undefined,
      storeName: null,
      storeIdsFilter: [],
      showTransactions: false,
      currentPage: 1,
      perPage: 50
    };
  },

  computed: {
    ...mapGetters(["storeBeingManaged", "transactions", "permissions"]),

    transactionsReverse() {
      return [...this.transactions].reverse();
    },

    transactionsDisplay() {
      if (!this.storeIdsFilter.length) return this.transactionsReverse;
      return this.transactionsReverse.filter(t =>
        this.storeIdsFilter.includes(t.store && t.store._id)
      );
    },

    totalPages() {
      return Math.max(
        1,
        Math.ceil(this.transactionsDisplay.length / this.perPage)
      );
    },

    transactionsPaged() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.transactionsDisplay.slice(start, start + this.perPage);
    }
  },

  methods: {
    getTransactions() {
      this.isLoading = true;
      this.currentPage = 1;
      this.$store
        .dispatch("getTransactions", {
          storeId: this.store,
          dates: this.dates
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    onPageChange(page) {
      this.currentPage = page;
      this.$nextTick(() => {
        const panel = document.getElementById("transactions-panel");
        if (panel) panel.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },

    downloadTransactionsReport() {
      this.isLoading = true;
      this.$store
        .dispatch("downloadTransactionsReport", {
          storeName: this.storeName,
          storeId: this.store,
          storeIds: this.storeIdsFilter,
          dates: this.dates
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.alertReportDowloadMessage();
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    formatDateStr(d) {
      const date = d ? new Date(d) : new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    updateDates(dates) {
      this.dates = [
        this.formatDateStr(dates ? dates[0] : null),
        this.formatDateStr(dates ? dates[1] : null)
      ];
    },

    updateStore(payload) {
      if (!payload) {
        this.store = "";
        this.storeName = "Todas as lojas";
        this.storeIdsFilter = [];
      } else if (payload.type === "store") {
        this.store = payload.storeId;
        this.storeName = payload.storeName;
        this.storeIdsFilter = [];
      } else if (payload.type === "group") {
        this.store = "";
        this.storeName = payload.label;
        this.storeIdsFilter = payload.storeIds;
      }
      this.getTransactions();
    }
  },

  mounted() {
    this.updateDates();
    this.getTransactions();
  },

  created() {
    if (this.storeBeingManaged && this.storeBeingManaged._id) {
      this.store = this.storeBeingManaged._id;
      this.storeName = this.storeBeingManaged.storeInfo.name;
    }
  }
};
</script>

<style scoped>
#transactions-panel {
  min-height: 100vh;
  background-color: #f8f7fa;
  padding: 1.5rem;
}

.page-inner {
  width: 100%;
}

.content-panel {
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07), 0 1px 2px rgba(0, 0, 0, 0.04);
  border: 1px solid #f3f4f6;
  padding: 1.5rem;
}

.empty-state {
  padding: 3rem 0;
  color: #9ca3af;
  text-align: center;
  font-size: 0.95rem;
}

.lgpd-notice {
  font-size: 0.8rem;
  color: #9ca3af;
  text-align: center;
  margin-bottom: 1rem;
}

.see-transactions-wrap {
  display: flex;
  justify-content: center;
  padding: 1.5rem 0 0.5rem;
}

.see-transactions-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  background: linear-gradient(135deg, #ff924c, #ff5c1a);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1.4rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.1s ease;
  box-shadow: 0 2px 8px rgba(255, 92, 26, 0.3);
}

.see-transactions-btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.see-transactions-btn:active {
  transform: translateY(0);
}

.see-icon {
  font-size: 1.1rem;
}
</style>
